import React, { useState } from 'react';
import { Container, Row, Col, Button, Spinner } from 'react-bootstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import instagram from '../../images/icons/instagram.svg';
import linkedin from '../../images/icons/linkedin.svg';
import envelope from '../../images/icons/envelope.svg';
import shield from '../../images/icons/shield.svg';
import './Contact.css';
import emailjs from 'emailjs-com';
import { useLanguage } from '../../context/LanguageContext';
import { isRateLimitExceeded, recordSubmission } from '../../utils/rateLimiter';

// Initialize EmailJS with the public key
emailjs.init('LMQgl-zTJeKgub20d');

const Contact: React.FC = () => {
  const { t } = useLanguage();

  const [form, setForm] = useState({
    nombre: '',
    apellido: '',
    empresa: '',
    email: '',
    rol: '',
    necesitas: '',
    horizonte: '',
    mensaje: ''
  });
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);

  const necesitasOptions = [
    { value: 'consulting', label: 'Consultoría / Consulting' },
    { value: 'development', label: 'Desarrollo / Development' },
    { value: 'training', label: 'Capacitación / Training' },
    { value: 'ai-search', label: 'Búsqueda IA / AI Search' },
    { value: 'ai-platform', label: 'Plataforma IA / AI Platform' },
    { value: 'custom-programming', label: 'Programación Personalizada / Custom Programming' },
    { value: 'mobile-apps', label: 'Aplicaciones Móviles / Mobile Apps' },
    { value: 'web-platforms', label: 'Plataformas Web / Web Platforms' },
    { value: 'other', label: 'Otro / Other' }
  ];

  const horizonteOptions = [
    { value: 'inmediato', label: 'Inmediato / Immediate' },
    { value: '1-3-meses', label: '1-3 meses / 1-3 months' },
    { value: '3-6-meses', label: '3-6 meses / 3-6 months' },
    { value: '6-plus-meses', label: '6+ meses / 6+ months' },
    { value: 'no-definido', label: 'No definido / Not defined' }
  ];

  const validate = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.nombre.trim()) newErrors.nombre = t('contact.form.validation.required');
    if (!form.apellido.trim()) newErrors.apellido = t('contact.form.validation.required');
    if (!form.email.trim()) {
      newErrors.email = t('contact.form.validation.required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = t('contact.form.validation.email');
    }
    if (!form.empresa.trim()) newErrors.empresa = t('contact.form.validation.required');
    if (!form.necesitas) newErrors.necesitas = t('contact.form.validation.required');
    return newErrors;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);

    // Check rate limit by email
    if (isRateLimitExceeded(form.email)) {
      setError('Límite de envíos excedido. Por favor intenta más tarde.');
      return;
    }

    // Check reCAPTCHA verification
    if (!recaptchaToken) {
      setError('Por favor verifica el reCAPTCHA.');
      return;
    }

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSending(true);

    const SERVICE_ID = 'service_n73j17b';
    const PUBLIC_KEY = 'LMQgl-zTJeKgub20d';

    const formattedMessage = `
**NUEVA SOLICITUD DE CONTACTO**

📋 Datos del Contacto:
- Nombre: ${form.nombre} ${form.apellido}
- Email: ${form.email}
- Empresa: ${form.empresa}

🎯 Detalles de la Solicitud:
- ¿Qué necesitas?: ${form.necesitas}

💬 Mensaje:
${form.mensaje || '(No hay mensaje adicional)'}
    `;

    try {
      await emailjs.send(
        SERVICE_ID,
        'template_dd65h97',
        {
          subject: `Nueva Solicitud de Contacto - ${form.nombre} ${form.apellido}`,
          message: formattedMessage,
        },
        PUBLIC_KEY
      );
      
      // Record submission for rate limiting
      recordSubmission(form.email);
      
      setSuccess('¡Mensaje enviado correctamente!');
      setForm({ nombre: '', apellido: '', empresa: '', email: '', rol: '', necesitas: '', horizonte: '', mensaje: '' });
      setErrors({});
      setRecaptchaToken(null);
    } catch (err) {
      console.error('EmailJS send error:', err);
      setError('Ocurrió un error al enviar el mensaje. Intenta nuevamente.');
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="contact-section" id="contact-section">
      <Container>
        <div className="contact-container">
          <Row>
            <Col lg={8}>
              <div className="contact-header mb-5">
                <p className="contact-subtitle">¿HABLAMOS?</p>
                <h2 className="contact-title">
                  Contanos sobre <span className="highlight">tu proyecto</span>
                </h2>
              </div>
              <div className="contact-form-container full-width">
                {success && (
                  <div className="custom-success-message">
                    <span className="success-icon">✓</span>
                    {success}
                  </div>
                )}
                {error && (
                  <div className="custom-error-message">
                    <span className="error-icon">✕</span>
                    {error}
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  {/* Nombre and Apellido */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t('contact.form.labels.nombre')}</label>
                      <input
                        type="text"
                        name="nombre"
                        value={form.nombre}
                        onChange={handleChange}
                        className={errors.nombre ? 'is-invalid' : ''}
                      />
                      {errors.nombre && <span className="error-text">{errors.nombre}</span>}
                    </div>
                    <div className="form-group">
                      <label>Apellido</label>
                      <input
                        type="text"
                        name="apellido"
                        value={form.apellido}
                        onChange={handleChange}
                        className={errors.apellido ? 'is-invalid' : ''}
                      />
                      {errors.apellido && <span className="error-text">{errors.apellido}</span>}
                    </div>
                  </div>

                  {/* Empresa and Email */}
                  <div className="form-row">
                    <div className="form-group">
                      <label>Empresa</label>
                      <input
                        type="text"
                        name="empresa"
                        value={form.empresa}
                        onChange={handleChange}
                        className={errors.empresa ? 'is-invalid' : ''}
                      />
                      {errors.empresa && <span className="error-text">{errors.empresa}</span>}
                    </div>
                    <div className="form-group">
                      <label>{t('contact.form.labels.email')}</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className={errors.email ? 'is-invalid' : ''}
                      />
                      {errors.email && <span className="error-text">{errors.email}</span>}
                    </div>
                  </div>

                  {/* ¿Qué necesitas? */}
                  <div className="form-group full-width">
                    <label>¿Qué necesitas?</label>
                    <select
                      name="necesitas"
                      value={form.necesitas}
                      onChange={handleChange}
                      className={errors.necesitas ? 'is-invalid' : ''}
                    >
                      <option value="">Elige una opción</option>
                      {necesitasOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    {errors.necesitas && <span className="error-text">{errors.necesitas}</span>}
                  </div>

                  {/* Mensaje */}
                  <div className="form-group full-width">
                    <label>Mensaje (Opcional)</label>
                    <textarea
                      name="mensaje"
                      value={form.mensaje}
                      onChange={handleChange}
                      rows={4}
                    />
                  </div>

                  {/* reCAPTCHA */}
                  <div className="form-group full-width" style={{ marginBottom: '1.5rem' }}>
                    <ReCAPTCHA
                      sitekey="6LdV5y8tAAAAAO-qNa0VSR14gAjPeWmbC6-bcWek"
                      onChange={(token: string | null) => setRecaptchaToken(token)}
                    />
                  </div>

                  <div className="form-submit-container">
                    <button type="submit" className="submit-button" disabled={sending}>
                      {sending ? (
                        <>
                          <Spinner size="sm" animation="border" className="me-2" />
                          Enviando...
                        </>
                      ) : (
                        'Quiero analizar mi proyecto'
                      )}
                    </button>
                    <div className="response-info">
                      <img src={shield} alt="Protegido" className="shield-icon" />
                      <p>Te responderemos dentro de 24 horas.</p>
                    </div>
                  </div>
                </form>
              </div>
            </Col>
            {/* <Col lg={4}>
              <div className="social-links-container">
                <h2 className="section-subtitle">{t('contact.social.title')}</h2>
                <div className="contact-info text-font" style={{ fontSize: '1rem', opacity: 0.9 }}>
                  <p>
                    <strong>Email:</strong><br />
                    info@lucods.com / ventas@lucods.com
                  </p>
                  <p>
                    <strong>Teléfono:</strong><br />
                    11-5491-6006
                  </p>
                </div>
                <div className="social-icons">
                  <a href="https://www.instagram.com/lucods_tech/?hl=es" target="_blank" rel="noopener noreferrer" className="social-icon-wrapper">
                    <img src={instagram} alt="Instagram" className="social-icon" />
                  </a>
                  <a href="https://ar.linkedin.com/company/lucods" target="_blank" rel="noopener noreferrer" className="social-icon-wrapper">
                    <img src={linkedin} alt="LinkedIn" className="social-icon" />
                  </a>
                </div>
              </div>
            </Col> */}
          </Row>
        </div>
      </Container>
    </section>
  );
};

export default Contact;
